// ─────────────────────────────────────────
//  SAVE / LOAD / PROJECTS
// ─────────────────────────────────────────

import { S, sb, ls, setLs } from './state.js';
import { esc, showToast, trackSignal } from './utils.js';
import { applyCode } from './editor.js';
import { runCode } from './preview.js';

export function openSave() {
  if (!S.code.trim()) { showToast('⚠ Build something first!'); return; }
  document.getElementById('save-modal')?.classList.add('open');
  setTimeout(() => document.getElementById('save-name')?.focus(), 300);
}

export function closeSave(e) {
  const modal = document.getElementById('save-modal');
  if (!e || e.target === modal) {
    modal?.classList.remove('open');
  }
}

export function saveProject() {
  const nameEl = document.getElementById('save-name');
  const descEl = document.getElementById('save-desc');
  const name = nameEl?.value.trim() || '';
  if (!name) { if (nameEl) nameEl.style.borderColor = 'var(--red)'; return; }
  const desc = descEl?.value.trim() || '';
  const ps = getProjects();

  // Update existing project if we have one tracked
  if (S.localProjectId) {
    const existing = ps.find(p => p.id === S.localProjectId);
    if (existing) {
      existing.name = name;
      existing.desc = desc;
      existing.code = S.code;
      existing.mode = S.mode;
      existing.saved = new Date().toLocaleDateString('en-ZA');
      setLs('vc_projects', JSON.stringify(ps));
      document.getElementById('save-modal')?.classList.remove('open');
      if (nameEl) nameEl.value = '';
      if (descEl) descEl.value = '';
      showToast('✓ Updated: ' + name);
      return;
    }
  }

  // Create new project
  const newId = Date.now().toString();
  ps.unshift({
    id: newId, name, desc,
    code: S.code, mode: S.mode,
    saved: new Date().toLocaleDateString('en-ZA'),
  });
  S.localProjectId = newId;
  setLs('vc_projects', JSON.stringify(ps));
  document.getElementById('save-modal')?.classList.remove('open');
  if (nameEl) nameEl.value = '';
  if (descEl) descEl.value = '';
  trackSignal('save');
  showToast('✓ Saved: ' + name);
}

export function autoSaveProject() {
  if (!S.code || S.code.trim().length < 50) return;

  const firstMsg = S.history.find(m => m.role === 'user')?.content || '';
  const autoName = firstMsg.slice(0, 50) || 'Untitled App';
  const ps = getProjects();

  // Update existing project if we're tracking one
  if (S.localProjectId) {
    const existing = ps.find(p => p.id === S.localProjectId);
    if (existing) {
      existing.code = S.code;
      existing.mode = S.mode;
      existing.saved = new Date().toLocaleDateString('en-ZA');
      setLs('vc_projects', JSON.stringify(ps));
      // Cloud sync (fire and forget)
      cloudSaveProject(existing);
      return;
    }
  }

  // Check if we already auto-saved this code (by matching first 200 chars)
  const codeStart = S.code.slice(0, 200);
  const existing = ps.find(p => p.code && p.code.slice(0, 200) === codeStart);
  if (existing) {
    existing.code = S.code;
    existing.mode = S.mode;
    existing.saved = new Date().toLocaleDateString('en-ZA');
    S.localProjectId = existing.id;
    setLs('vc_projects', JSON.stringify(ps));
    cloudSaveProject(existing);
    return;
  }

  // Create new auto-save
  const newId = Date.now().toString();
  const newProject = {
    id: newId,
    name: autoName,
    desc: '',
    code: S.code,
    mode: S.mode,
    saved: new Date().toLocaleDateString('en-ZA'),
    autoSaved: true,
  };
  ps.unshift(newProject);
  S.localProjectId = newId;

  // Keep max 20 projects to avoid localStorage bloat
  if (ps.length > 20) ps.length = 20;

  setLs('vc_projects', JSON.stringify(ps));

  // Cloud sync (fire and forget)
  cloudSaveProject(newProject);
}

function cloudSaveProject(project) {
  if (!S.session) return;

  const body = {
    id: project.remoteId || undefined,
    name: project.name || 'Untitled',
    description: project.desc || '',
    code: project.code,
    mode: project.mode || 'html',
  };

  fetch('/api/projects', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': 'Bearer ' + S.session.access_token,
    },
    body: JSON.stringify(body),
  })
    .then(res => res.json())
    .then(data => {
      if (data.project?.id && !project.remoteId) {
        // Store the remote ID locally for future updates
        project.remoteId = data.project.id;
        if (data.project.share_slug) project.shareSlug = data.project.share_slug;
        const ps = getProjects();
        const local = ps.find(p => p.id === project.id);
        if (local) {
          local.remoteId = data.project.id;
          if (data.project.share_slug) local.shareSlug = data.project.share_slug;
          setLs('vc_projects', JSON.stringify(ps));
        }
      }
    })
    .catch(() => {}); // Silent fail
}

export function getProjects() {
  try { return JSON.parse(ls('vc_projects')||'[]'); } catch { return []; }
}

export function openDrawer() {
  renderProjects();
  document.getElementById('drawer-overlay')?.classList.add('open');
  document.getElementById('projects-drawer')?.classList.add('open');

  // Sync from cloud in background
  syncProjectsFromCloud();
}

async function syncProjectsFromCloud() {
  if (!S.session) return;

  try {
    const res = await fetch('/api/projects', {
      headers: { 'Authorization': 'Bearer ' + S.session.access_token },
    });
    if (!res.ok) return;

    const { projects: remoteProjects } = await res.json();
    if (!remoteProjects || !remoteProjects.length) return;

    const localProjects = getProjects();
    let changed = false;

    for (const remote of remoteProjects) {
      // Check if we already have this project locally (by remoteId)
      const existing = localProjects.find(p => p.remoteId === remote.id);
      if (existing) {
        // Update metadata if remote is newer
        if (remote.updated_at && existing.saved) {
          const remoteDate = new Date(remote.updated_at);
          const localParts = existing.saved.split('/');
          // Only update name/desc from remote, keep local code (it's more current)
          if (remote.name && remote.name !== existing.name) {
            existing.name = remote.name;
            changed = true;
          }
        }
      } else {
        // New remote project not in local — add it
        localProjects.push({
          id: Date.now().toString() + Math.random().toString(36).slice(2, 6),
          remoteId: remote.id,
          name: remote.name || 'Untitled',
          desc: remote.description || '',
          code: '', // Don't download full code yet — load on demand
          mode: remote.mode || 'html',
          saved: remote.updated_at ? new Date(remote.updated_at).toLocaleDateString('en-ZA') : '',
          shareSlug: remote.share_slug || null,
          isRemoteOnly: true,
        });
        changed = true;
      }
    }

    if (changed) {
      setLs('vc_projects', JSON.stringify(localProjects));
      renderProjects();
    }
  } catch (_) {
    // Silent fail — cloud sync is best-effort
  }
}

export function closeDrawer() {
  document.getElementById('drawer-overlay')?.classList.remove('open');
  document.getElementById('projects-drawer')?.classList.remove('open');
}

export function renderProjects() {
  const ps = getProjects();
  const list = document.getElementById('projects-list');
  const countEl = document.getElementById('drawer-count');
  if (!list) return;

  if (countEl) countEl.textContent = ps.length ? `${ps.length} project${ps.length !== 1 ? 's' : ''}` : '';

  if (!ps.length) {
    list.innerHTML = `<div class="empty-projects">
      <div class="ep-icon">📂</div>
      No saved projects yet.<br>
      Build something and tap 💾 to save it!
    </div>`;
    return;
  }

  list.innerHTML = ps.map(p => {
    const mode = (p.mode || 'html').toUpperCase();
    const saved = p.saved || '';
    const isCloud = p.isRemoteOnly && !p.code;
    const views = p.viewCount || 0;
    const published = p.isPublished;
    const subdomain = p.subdomain;

    const statsHtml = views > 0 || published
      ? `<div class="proj-row-stats">${views > 0 ? '<span>👁 ' + views + '</span>' : ''}${published ? '<span class="proj-published">🌐 ' + (subdomain || 'published') + '</span>' : ''}</div>`
      : '';

    return `
    <div class="proj-row" data-id="${p.id}">
      <div class="proj-row-main" onclick="loadProject('${p.id}')">
        <span class="proj-row-icon">${isCloud ? '☁️' : published ? '🌐' : '📄'}</span>
        <div class="proj-row-text">
          <div class="proj-row-name">${esc(p.name)}</div>
          <div class="proj-row-meta">${mode} · ${saved}${views > 0 ? ' · 👁 ' + views : ''}</div>
        </div>
      </div>
      <button class="proj-row-del" onclick="event.stopPropagation(); confirmDelProject('${p.id}')" aria-label="Delete project">🗑</button>
    </div>`;
  }).join('');

  // Fetch analytics in background to update view counts
  fetchProjectAnalytics();
}

// Fetch analytics from server and update local project data
async function fetchProjectAnalytics() {
  if (!S.session) return;

  try {
    const res = await fetch('/api/projects/analytics', {
      headers: { 'Authorization': 'Bearer ' + S.session.access_token },
    });
    if (!res.ok) return;

    const { projects: analyticsData, totals } = await res.json();
    if (!analyticsData) return;

    const localProjects = getProjects();
    let changed = false;

    for (const remote of analyticsData) {
      const local = localProjects.find(p => p.remoteId === remote.id);
      if (local) {
        if (local.viewCount !== remote.views || local.isPublished !== remote.isPublished) {
          local.viewCount = remote.views;
          local.remixCount = remote.remixes;
          local.isPublished = remote.isPublished;
          local.subdomain = remote.subdomain;
          changed = true;
        }
      }
    }

    if (changed) {
      setLs('vc_projects', JSON.stringify(localProjects));
      // Re-render to show updated stats
      const list = document.getElementById('projects-list');
      if (list && document.getElementById('projects-drawer')?.classList.contains('open')) {
        renderProjects();
      }
    }

    // Show totals in drawer header if available
    if (totals && totals.views > 0) {
      const countEl = document.getElementById('drawer-count');
      if (countEl) {
        countEl.textContent = `${localProjects.length} projects · ${totals.views} total views`;
      }
    }
  } catch (_) {}
}

export async function loadProject(id) {
  const ps = getProjects();
  const p = ps.find(x => x.id === id);
  if (!p) return;

  // If remote-only (no code stored locally), fetch from cloud
  if (p.isRemoteOnly && !p.code && p.remoteId && S.session) {
    showToast('Loading project...');
    try {
      const res = await fetch('/api/projects?id=' + p.remoteId, {
        headers: { 'Authorization': 'Bearer ' + S.session.access_token },
      });
      if (res.ok) {
        const { projects } = await res.json();
        const remote = projects?.find(r => r.id === p.remoteId);
        if (remote?.code) {
          p.code = remote.code;
          p.mode = remote.mode || 'html';
          p.isRemoteOnly = false;
          setLs('vc_projects', JSON.stringify(ps));
        }
      }
    } catch (_) {}

    if (!p.code) {
      showToast('⚠ Could not load project from cloud');
      return;
    }
  }

  S.localProjectId = id;
  S.projectId = p.remoteId || null;
  applyCode(p.code);
  S.mode = p.mode || 'html';
  runCode();
  closeDrawer();
  showToast('✓ Loaded: ' + p.name);

  // Load project memory from Supabase (if linked conversation exists)
  loadProjectMemory(p.remoteId);
}

async function loadProjectMemory(remoteProjectId) {
  if (!S.session || !remoteProjectId) return;

  try {
    const { data } = await sb.from('conversations')
      .select('id, messages, memory')
      .eq('user_id', S.session.user.id)
      .eq('project_id', remoteProjectId)
      .order('created_at', { ascending: false })
      .limit(1)
      .single();

    if (data) {
      S.convId = data.id;
      S.memory = data.memory || '';
      // Restore history from the linked conversation
      if (data.messages && data.messages.length > 0) {
        S.history = data.messages;
      }
    }
  } catch (_) {
    // No linked conversation — start fresh
  }
}

let _pendingDeleteId = null;

export function confirmDelProject(id) {
  const ps = getProjects();
  const p = ps.find(x => x.id === id);
  if (!p) return;

  _pendingDeleteId = id;

  const modal = document.getElementById('del-confirm-modal');
  const nameDisplay = document.getElementById('del-confirm-name');
  const input = document.getElementById('del-confirm-input');
  const btn = document.getElementById('del-confirm-btn');

  if (nameDisplay) nameDisplay.textContent = p.name;
  if (input) { input.value = ''; input.dataset.expected = p.name; }
  if (btn) btn.disabled = true;

  modal?.classList.add('open');
  setTimeout(() => input?.focus(), 300);
}

export function closeDelConfirm(e) {
  const modal = document.getElementById('del-confirm-modal');
  if (!e || e.target === modal) {
    modal?.classList.remove('open');
    _pendingDeleteId = null;
  }
}

export function onDelConfirmInput() {
  const input = document.getElementById('del-confirm-input');
  const btn = document.getElementById('del-confirm-btn');
  if (!input || !btn) return;
  btn.disabled = input.value.trim() !== input.dataset.expected;
}

export function executeDelete() {
  if (!_pendingDeleteId) return;
  const input = document.getElementById('del-confirm-input');
  if (!input || input.value.trim() !== input.dataset.expected) return;

  const ps = getProjects().filter(x => x.id !== _pendingDeleteId);
  setLs('vc_projects', JSON.stringify(ps));
  _pendingDeleteId = null;

  document.getElementById('del-confirm-modal')?.classList.remove('open');
  renderProjects();
  showToast('🗑 Deleted');
}

export function delProject(id) {
  // Redirect to safe confirmation flow
  confirmDelProject(id);
}

export function moveProject(id, direction) {
  const ps = getProjects();
  const idx = ps.findIndex(p => p.id === id);
  if (idx < 0) return;
  const newIdx = idx + direction;
  if (newIdx < 0 || newIdx >= ps.length) return;
  [ps[idx], ps[newIdx]] = [ps[newIdx], ps[idx]];
  setLs('vc_projects', JSON.stringify(ps));
  renderProjects();
}

// Drag and drop reordering
let _dragId = null;

export function dragProject(e, id) {
  _dragId = id;
  e.currentTarget.classList.add('dragging');
  e.dataTransfer.effectAllowed = 'move';
}

export function dragOverProject(e) {
  e.preventDefault();
  e.currentTarget.classList.add('drag-over');
}

export function dropProject(e, targetId) {
  e.preventDefault();
  e.currentTarget.classList.remove('drag-over');
  if (!_dragId || _dragId === targetId) return;
  const ps = getProjects();
  const fromIdx = ps.findIndex(p => p.id === _dragId);
  const toIdx = ps.findIndex(p => p.id === targetId);
  if (fromIdx < 0 || toIdx < 0) return;
  const [moved] = ps.splice(fromIdx, 1);
  ps.splice(toIdx, 0, moved);
  setLs('vc_projects', JSON.stringify(ps));
  renderProjects();
  _dragId = null;
}

export function dragEndProject(e) {
  _dragId = null;
  document.querySelectorAll('.proj-card').forEach(c => {
    c.classList.remove('dragging', 'drag-over');
  });
}

export function shareProject(id) {
  const p = getProjects().find(x=>x.id===id);
  if (p) {
    S.code = p.code;
    S.mode = p.mode || 'html';
    document.getElementById('deploy-modal')?.classList.add('open');
  }
}
